import React, { useState, useEffect, useMemo } from 'react';
import '../styles/HelpDrawer.css';

const HelpDrawer = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('intro');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // Contact form states
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const helpContent = useMemo(() => ({
    intro: {
      title: '🌐 مقدمه',
      icon: 'fa-home',
      content: [
        {
          heading: 'درباره سامانه',
          text: 'به منظور تسهیل در فرآیند برگزاری انتخابات انجمن‌های علمی و فراهم‌سازی بستری شفاف، سریع و ایمن برای رأی‌گیری دانشجویان، تیم توسعه نرم‌افزاری آرمان با همکاری مدیریت مرکز رشد و کارآفرینی بین‌المللی دانشگاه شمال و مدیریت فرهنگی دانشگاه شمال اقدام به طراحی و راه‌اندازی این سامانه نموده است.'
        },
        {
          heading: 'اهداف سامانه',
          text: 'این سامانه با هدف حمایت از فعالیت‌های علمی دانشجویان، افزایش مشارکت دانشگاهیان در تصمیم‌گیری‌های علمی و ارتقای فرهنگ کار تیمی و انجمنی طراحی شده است.'
        },
        {
          heading: 'آدرس سامانه',
          text: 'http://anjomanshomal.ir',
          isHighlight: true
        }
      ]
    },
    candidates: {
      title: '👥 مشاهده نامزدها',
      icon: 'fa-users',
      content: [
        {
          heading: 'نحوه مشاهده نامزدها',
          text: 'با انتخاب گزینه «مشاهده نامزدها» از صفحه اصلی، می‌توانید:'
        },
        {
          isList: true,
          items: [
            'رشته تحصیلی خود را از میان رشته‌های موجود انتخاب کنید',
            'فهرست نامزدهای همان رشته را مشاهده نمایید',
            'در صورت وجود اطلاعات تکمیلی، با رزومه، اهداف و برنامه‌های هر نامزد آشنا شوید'
          ]
        },
        {
          heading: 'اهمیت آشنایی با نامزدها',
          text: 'این بخش به شما کمک می‌کند تا پیش از رأی دادن، شناخت دقیق‌تری از نامزدهای رشته خود داشته باشید و بتوانید تصمیم آگاهانه‌تری بگیرید.'
        }
      ]
    },
    voting: {
      title: '🗳 فرآیند رای‌گیری',
      icon: 'fa-vote-yea',
      content: [
        {
          heading: 'مراحل رای‌گیری',
          text: 'فرآیند رأی‌گیری در چهار مرحله انجام می‌گیرد:'
        },
        {
          isList: true,
          isNumbered: true,
          items: [
            '**انتخاب دانشکده**: دانشکده‌ی خود را از بین گزینه‌های موجود انتخاب کنید',
            '**انتخاب رشته**: رشته‌ی تحصیلی خود را برگزینید تا فهرست نامزدهای مرتبط نمایش داده شود',
            '**انتخاب نامزد**: نامزد یا نامزدهای مورد نظر (حداکثر تا ۷ نفر) را انتخاب کنید',
            '**ثبت نهایی رای**: اطلاعات دانشجویی خود (شماره دانشجویی و کد ملی) را وارد نمایید'
          ]
        },
        {
          heading: 'احراز هویت',
          text: 'با ثبت رأی، اطلاعات شما جهت احراز هویت بررسی می‌شود و هر دانشجو تنها یک‌بار مجاز به رأی دادن است.',
          isWarning: true
        },
        {
          heading: 'تأیید نهایی',
          text: 'پس از تکمیل اطلاعات و انتخاب دکمه‌ی «ثبت نهایی رأی»، رأی شما با موفقیت ثبت خواهد شد.'
        }
      ]
    },
    notes: {
      title: '📌 نکات مهم',
      icon: 'fa-exclamation-circle',
      content: [
        {
          heading: 'محدودیت‌های رأی‌گیری',
          isList: true,
          items: [
            '**یکبار رأی‌دهی**: هر دانشجو فقط یک‌بار امکان رأی دادن دارد',
            '**محدودیت رشته**: تنها می‌توانید به نامزدهای رشته‌ی خود رأی بدهید',
            '**تعداد نامزدها**: حداقل ۱ و حداکثر ۷ نامزد را می‌توانید انتخاب کنید'
          ],
          isWarning: true
        },
        {
          heading: 'موارد استثنا',
          isList: true,
          items: [
            '**دانشکده علوم ورزشی**: دانشجویان این دانشکده به دلیل وجود انجمن واحد دانشکده‌ای، می‌توانند از تمام رشته‌های دانشکده در رأی‌گیری شرکت کنند',
            '**رشته‌های بدون انجمن**: رشته‌هایی که انجمن فعال ندارند، مجازند در انتخابات انجمن رشته‌های دیگر در همان دانشکده شرکت کنند'
          ]
        },
        {
          heading: '🔒 امنیت و محرمانگی',
          text: 'تمامی داده‌ها و آرای ثبت‌شده در این سامانه به صورت رمزنگاری‌شده ذخیره می‌شوند و اطلاعات دانشجویان صرفاً برای تأیید هویت مورد استفاده قرار می‌گیرد. تیم توسعه نرم‌افزاری آرمان امنیت و صحت فرآیند رأی‌گیری را تضمین می‌کند.',
          isSuccess: true
        }
      ]
    },
    support: {
      title: '💬 پشتیبانی و ارتباط',
      icon: 'fa-headset',
      content: [
        {
          heading: 'ارتباط با تیم پشتیبانی',
          text: 'در صورت بروز هرگونه مشکل، انتقاد، پیشنهاد یا نظر، می‌توانید از طریق فرم زیر با ما در ارتباط باشید.'
        }
      ]
    }
  }), []);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      return;
    }

    const results = [];
    Object.entries(helpContent).forEach(([tabKey, tabData]) => {
      tabData.content.forEach((section, index) => {
        const searchText = `${section.heading || ''} ${section.text || ''} ${section.items?.join(' ') || ''}`;
        if (searchText.toLowerCase().includes(searchQuery.toLowerCase())) {
          results.push({
            tab: tabKey,
            tabTitle: tabData.title,
            section: section,
            index: index
          });
        }
      });
    });
    setSearchResults(results);
  }, [searchQuery, helpContent]);

  const handleSearchResultClick = (result) => {
    setActiveTab(result.tab);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      // Create mailto link
      const subject = encodeURIComponent(contactForm.subject || 'پیام از سامانه رای‌گیری');
      const body = encodeURIComponent(
        `نام: ${contactForm.name}\nایمیل: ${contactForm.email}\n\nپیام:\n${contactForm.message}`
      );
      
      // Open email client
      window.location.href = `mailto:info.armanteam@gmail.com?subject=${subject}&body=${body}`;
      
      setFormStatus({ 
        type: 'success', 
        message: 'برنامه ایمیل شما باز شد. لطفاً پیام را ارسال کنید.' 
      });
      
      // Reset form after 2 seconds
      setTimeout(() => {
        setContactForm({ name: '', email: '', subject: '', message: '' });
        setFormStatus({ type: '', message: '' });
      }, 2000);
      
    } catch (error) {
      setFormStatus({ 
        type: 'error', 
        message: 'خطا در ارسال پیام. لطفاً دوباره تلاش کنید.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const renderContent = (section) => {
    return (
      <div className={`help-section ${section.isWarning ? 'warning' : ''} ${section.isSuccess ? 'success' : ''} ${section.isHighlight ? 'highlight' : ''}`}>
        {section.heading && <h4>{section.heading}</h4>}
        {section.text && (
          section.isLink ? (
            <a href={section.link} target="_blank" rel="noopener noreferrer" className="help-link">
              <i className="fas fa-external-link-alt"></i>
              {section.text}
            </a>
          ) : (
            <p dangerouslySetInnerHTML={{ __html: section.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
          )
        )}
        {section.isList && (
          section.isNumbered ? (
            <ol className="help-list">
              {section.items.map((item, idx) => (
                <li key={idx} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
              ))}
            </ol>
          ) : (
            <ul className="help-list">
              {section.items.map((item, idx) => (
                <li key={idx} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
              ))}
            </ul>
          )
        )}
      </div>
    );
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className={`help-overlay ${isOpen ? 'active' : ''}`} 
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className={`help-drawer ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="help-drawer-header">
          <div className="help-header-content">
            <div className="help-header-title">
              <i className="fas fa-question-circle"></i>
              <h2>راهنمای سامانه</h2>
            </div>
            <p className="help-header-subtitle">انتخابات انجمن‌های علمی دانشگاه شمال</p>
          </div>
          <button className="help-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Search */}
        <div className="help-search-container">
          <div className="help-search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="جستجو در راهنما..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear-btn" onClick={() => setSearchQuery('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="search-results">
              <div className="search-results-header">
                <span>{searchResults.length} نتیجه یافت شد</span>
              </div>
              {searchResults.map((result, idx) => (
                <div 
                  key={idx} 
                  className="search-result-item"
                  onClick={() => handleSearchResultClick(result)}
                >
                  <div className="search-result-badge">{result.tabTitle}</div>
                  <div className="search-result-text">{result.section.heading || result.section.text?.substring(0, 60) + '...'}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="help-tabs">
          {Object.entries(helpContent).map(([key, data]) => (
            <button
              key={key}
              className={`help-tab ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <i className={`fas ${data.icon}`}></i>
              <span>{data.title}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="help-content">
          {activeTab === 'support' ? (
            <div className="contact-form-container">
              <div className="help-section">
                <h4>ارتباط با تیم پشتیبانی</h4>
                <p>در صورت بروز هرگونه مشکل، انتقاد، پیشنهاد یا نظر، می‌توانید از طریق فرم زیر با ما در ارتباط باشید.</p>
              </div>
              
              <form onSubmit={handleContactSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">
                    <i className="fas fa-user"></i>
                    نام و نام خانوادگی
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={contactForm.name}
                    onChange={handleContactChange}
                    placeholder="نام خود را وارد کنید"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i>
                    ایمیل
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleContactChange}
                    placeholder="example@gmail.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">
                    <i className="fas fa-tag"></i>
                    موضوع پیام
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={contactForm.subject}
                    onChange={handleContactChange}
                    required
                  >
                    <option value="">انتخاب کنید</option>
                    <option value="مشکل فنی">🔧 مشکل فنی</option>
                    <option value="پیشنهاد">💡 پیشنهاد</option>
                    <option value="انتقاد">📝 انتقاد</option>
                    <option value="تشکر و قدردانی">🙏 تشکر و قدردانی</option>
                    <option value="سایر">💬 سایر</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    <i className="fas fa-comment-dots"></i>
                    پیام شما
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactChange}
                    placeholder="پیام خود را اینجا بنویسید..."
                    rows="6"
                    required
                  ></textarea>
                </div>

                {formStatus.message && (
                  <div className={`form-status ${formStatus.type}`}>
                    <i className={`fas fa-${formStatus.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                    {formStatus.message}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="contact-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      در حال ارسال...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      ارسال پیام
                    </>
                  )}
                </button>
              </form>

              <div className="contact-info-box">
                <i className="fas fa-info-circle"></i>
                <p>پیام شما مستقیماً به ایمیل <strong>info.armanteam@gmail.com</strong> ارسال می‌شود</p>
              </div>
              
              <div className="help-section highlight" style={{marginTop: '20px'}}>
                <h4>سازندگان سامانه</h4>
                <p>طراحی و توسعه توسط تیم نرم‌افزاری آرمان</p>
              </div>
            </div>
          ) : (
            helpContent[activeTab].content.map((section, idx) => (
              <div key={idx}>
                {renderContent(section)}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="help-drawer-footer">
          <div className="help-footer-info">
            <i className="fas fa-info-circle"></i>
            <span>برای کمک بیشتر به بخش پشتیبانی مراجعه کنید</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpDrawer;